import React from 'react';
// @ts-ignore
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import { AriaLiveContextProvider } from 'hellospa/common/hooks/use-aria-live';

export const currency = 'USD';
export const symbol = '$';

declare global {
  interface Window {
    localeMessages?: Record<string, string>,
    locale?: string,
  }
}

// To activate this in development run this in the console and refresh the page:
// localStorage.intlMessages = {}
const storageKey = 'intlMessages';

export const { messages, locale } = (function ConvertMessages() {
  const { locale = 'en-US' } = window;
  const messages = {} as Record<string, string>;

  function collectSmartlingMessages(mess?: { [key: string]: { translation: string } }) {
    const messagesLocal = {} as Record<string, string>;
    if (mess) {
      const keys = Object.keys(mess);
      for (let i = 0; i < keys.length; i++) {
        const id = keys[i];
        messagesLocal[id] = mess[id].translation;
      }
    }
    return messagesLocal;
  }

  if (
    // QA uses a development build
    process.env.NODE_ENV !== 'production'
    && (
      window.location.host === 'app.dev-hellosign.com'
      || window.location.host === 'app.dev-hellofax.com'
    )
  ) {
    // localStorage my throw if it's being blocked
    try {
      if (localStorage[storageKey] != null) {
        // eslint-disable-next-line global-require
        const debugMessages = JSON.parse(localStorage[storageKey]);
        Object.assign(messages, collectSmartlingMessages(debugMessages.messages));
      }
    } catch (e) {
      // do nothing
    }
  }
  Object.assign(messages, window.localeMessages);
  return { messages, locale };
}());


// This is optional but highly recommended
// since it prevents memory leak
const cache = createIntlCache();

const [language] = locale.split('-');
/* eslint-disable global-require, comma-dangle, import/no-dynamic-require */
// SyntaxError: Trailing comma is disallowed inside import(...) arguments
if (!('PluralRules' in Intl)) {
  require('@formatjs/intl-pluralrules/polyfill');
  try {
    require(`@formatjs/intl-pluralrules/dist/locale-data/${language}`);
  } catch (e) {
    require('@formatjs/intl-pluralrules/dist/locale-data/en');
  }
}

if (!('RelativeTimeFormat' in Intl)) {
  require('@formatjs/intl-relativetimeformat/polyfill');
  try {
    require(`@formatjs/intl-relativetimeformat/dist/locale-data/${language}`);
  } catch (e) {
    require('@formatjs/intl-relativetimeformat/dist/locale-data/en');
  }
}

if (!('FormatList' in Intl)) {
  require('@formatjs/intl-listformat/polyfill');
  try {
    require(`@formatjs/intl-listformat/dist/locale-data/${language}`);
  } catch (e) {
    require('@formatjs/intl-listformat/dist/locale-data/en');
  }
}
/* eslint-enable global-require, comma-dangle, import/no-dynamic-require */


const errors: Record<string, boolean> = {};
export function onError(error: string) {
  if (process.env.NODE_ENV !== 'production') {
    // Suppress errors about missing messages. We never supply messages for
    // English, so they're always "missing"
    if (!window.localeMessages && error.match(/Missing message: "/)) {
      return;
    }

    // Only show the error once. The default implementation spams the console
    // on every render.
    if (!errors[error]) {
      errors[error] = true;
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}

// Please try to avoid having to import this singleton directly. Doing so makes
// this a compile-time dependency instead of a runtime dependency. That will
// cause problems if we want to do things like load our messages in chunks.
// `intl` can't be modified, it just has to get replaced, and we can't do that
// when things import this directly.
export const intl = createIntl({
  locale,
  messages: {
    ...messages,
  },
  onError,
}, cache);

type Props = {
  children: React.ReactNode,
}

export default function HSIntlProvider({ children }: Props) {
  return (
    <RawIntlProvider value={intl}>
      <AriaLiveContextProvider>
        {children}
      </AriaLiveContextProvider>
    </RawIntlProvider>
  );
}
